import React from "react";
import { PanelRightClose, PanelRightOpen } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import type { DetailsSidebarProps } from "./DetailsSidebar.types";

export const DetailsSidebar: React.FC<DetailsSidebarProps> = ({
  title,
  eyebrow = "Details",
  subtitle,
  status,
  actions,
  children,
  emptyTitle = "Select a row",
  emptyDescription = "Click a table row to inspect details and available actions.",
  collapsed,
  onCollapsedChange,
  className,
}) => {
  if (collapsed) {
    return (
      <aside
        className={cn(
          "sticky top-20 flex min-h-[24rem] flex-col items-center gap-3 rounded-lg border",
          "border-slate-200 bg-white px-2 py-3 dark:border-slate-800 dark:bg-slate-900",
          className,
        )}
        aria-label={`${title} collapsed details`}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => onCollapsedChange(false)}
          aria-label="Expand details sidebar"
        >
          <PanelRightOpen className="h-4 w-4" />
        </Button>
        <span className="mt-2 rotate-180 text-[11px] font-semibold uppercase tracking-wide text-slate-500 [writing-mode:vertical-rl] dark:text-slate-400">
          Details
        </span>
      </aside>
    );
  }

  return (
    <aside
      className={cn(
        "sticky top-20 max-h-[calc(100vh-7rem)] overflow-hidden rounded-lg border",
        "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900",
        className,
      )}
      aria-label={`${title} details`}
    >
      <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {eyebrow}
          </p>
          <h2 className="mt-1 truncate text-sm font-semibold text-slate-950 dark:text-slate-50">
            {title}
          </h2>
          {subtitle ? (
            <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {status}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => onCollapsedChange(true)}
            aria-label="Collapse details sidebar"
          >
            <PanelRightClose className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {actions ? (
        <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <div className="flex flex-wrap gap-2">{actions}</div>
        </div>
      ) : null}

      <div className="max-h-[calc(100vh-14rem)] overflow-auto p-4">
        {children ? (
          children
        ) : (
          <div className="rounded-lg border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
            <p className="font-medium text-slate-700 dark:text-slate-200">
              {emptyTitle}
            </p>
            <p className="mt-1">{emptyDescription}</p>
          </div>
        )}
      </div>
    </aside>
  );
};
